import React, { useState } from 'react';
import { Box, Button, Group, Stepper, NumberInput, Card, Title, Grid, Divider, Text, Stack, Modal, ActionIcon, Tabs, Badge, Loader, ThemeIcon, Tooltip, LoadingOverlay } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconX, IconArrowRight, IconArrowLeft, IconCheck, IconFileTypePdf, IconEye, IconBriefcase, IconTruck, IconBuildingSkyscraper, IconStairs, IconEdit, IconCloudCheck, IconCloudX, IconRestore, IconHistory, IconCalendarStats } from '@tabler/icons-react';
import type { ICotizacionNBS } from '../../../modelos/CotizadorNBS/entidades/ICotizacionNBS';
import type { IEquipoAscensor } from '../../../modelos/CotizadorNBS/entidades/IEquipoAscensor';
import type { IEquipoEscalera } from '../../../modelos/CotizadorNBS/entidades/IEquipoEscalera';

import type { IResultadosEquipo } from '../../../modelos/CotizadorNBS/entidades/IResultadosEquipo';
import { puedeGenerarPdf, type EstadoCotizacionNBSId } from '../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS';
import { BadgeEstadoCotizacionVista } from './badgeEstadoCotizacionVista';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { EnumTipoNotificacion } from '../../../globales/interfaces';
import { CotizadorModelo, type IOpcionesEquiposNBS } from '../../../modelos/CotizadorNBS';

import { PasoProyectoVista } from './stepper/PasoProyectoVista';
import { PasoEquiposVista } from './stepper/PasoEquiposVista';
import { PasoResumenVista } from './stepper/PasoResumenVista';
import { HistorialEstadosCV } from '../controladorVista/historialEstadosCV';
import type { IConfiguracionState } from '../../configuracionCotizadorNbs/interfaces';
import type { IVariacionMensualMes } from '../../../modelos/CotizadorNBS/entidades/IVariacionMensualMes';
import { ModalVariacionMensualVista, type IEquipoVariacionMensual } from './stepper/ModalVariacionMensualVista';
import { textoCapacidadAscensor } from '../utils/especificacionesAscensor';
import { usePermisos } from '../../../globales/contextos/usePermisos';
import { EnumPermisos } from '../../../globales/enums/enumPermisos';
import { mesesMantenimientoValidos } from '../interfaces';

const RESULTADOS_VACIOS = new Proxy({} as IResultadosEquipo, {
    get: () => 0
});

interface CrearCotizacionNBSVistaProps {
    cotizacion: ICotizacionNBS;
    pasoInicial?: number;
    alCambiarProyecto: (campo: keyof ICotizacionNBS['Proyecto'], valor: any) => void;
    alGuardar: () => void;
    /** Cambio manual de estado (Revisión Interna/Cliente, Rechazada, Aceptada). */
    alCambiarEstado?: (estado: EstadoCotizacionNBSId) => void;
    alAutoguardar?: () => void;
    alAvanzarPaso1?: () => Promise<void>;
    alAvanzarPaso2?: () => Promise<void>;
    alCancelar: () => void;
    agregarAscensor?: (ascensor: IEquipoAscensor) => Promise<void>;
    agregarEscalera?: (escalera: IEquipoEscalera) => Promise<void>;
    editarAscensor?: (ascensor: IEquipoAscensor) => void;
    editarEscalera?: (escalera: IEquipoEscalera) => void;
    eliminarEquipo?: (Id: string, Tipo: 'ascensor' | 'escalera') => void;
    /** Contenedores de un ascensor corregidos a mano (null = volver al calculado). true si quedó guardado. */
    alActualizarContenedores?: (equipoId: string, cantidadContenedores: number | null) => Promise<boolean>;
    /** Variables de configuración solo para esta cotización. true si quedó guardado. */
    alGuardarConfiguracion?: (configuracion: IConfiguracionState) => Promise<boolean>;
    /** Vuelve a la configuración global vigente y recalcula. */
    alUsarConfiguracionGlobal?: () => void;
    /** Recalcula con las matrices vigentes (aviso de matrices modificadas). */
    alRecalcularConMatricesVigentes?: () => void;
    ciudades?: {value: string, label: string}[];
    /** Variación mensual del flete del país (popup informativo en el Resumen). */
    variacionMensual?: IVariacionMensualMes[];
    /** Panamá: el paso 1 muestra el switch "Aplica pólizas". */
    esPanama?: boolean;
}



export const CrearCotizacionNBSVista: React.FC<CrearCotizacionNBSVistaProps> = ({
    cotizacion,
    esPanama = false,
    pasoInicial = 0,
    alCambiarProyecto,
    alGuardar,
    alCambiarEstado,
    alAutoguardar,
    alAvanzarPaso1,
    alAvanzarPaso2,
    alCancelar,
    agregarAscensor,
    agregarEscalera,
    editarAscensor,
    editarEscalera,
    eliminarEquipo,
    alActualizarContenedores,
    alGuardarConfiguracion,
    alUsarConfiguracionGlobal,
    alRecalcularConMatricesVigentes,
    ciudades,
    variacionMensual = []
}) => {
    const [active, setActive] = useState(pasoInicial);
    const [historialAbierto, setHistorialAbierto] = useState(false);
    // Popup informativo de la variación mensual del flete (pestaña Rentabilidad del detalle del equipo).
    const [equipoVariacion, setEquipoVariacion] = useState<IEquipoVariacionMensual | null>(null);
    const [calculandoTotales, setCalculandoTotales] = useState(false);

    // Para no bloquear la UI si el usuario quiere avanzar de paso en paso
    const [estaGuardado, setEstaGuardado] = useState(true);
    const [estaAutoguardando, setEstaAutoguardando] = useState(false);
    const [errorGuardado, setErrorGuardado] = useState(false);


    // Solo Aceptada es de solo lectura (regla del back).
    const esSoloLectura = cotizacion.PermiteEdicion === false;

    // Por qué no se puede generar el PDF (null = se puede). En Aceptada siempre se puede descargar.
    const motivoSinPdf = esSoloLectura ? null
        : !puedeGenerarPdf(cotizacion.Estado?.Id as number) ? 'Completa el cálculo de la cotización para generar el PDF.'
        : cotizacion.RequiereRecalculo ? 'Hay cambios sin calcular: recalcula la cotización para generar el PDF.'
        : !cotizacion.CalculoCompleto ? 'Faltan datos en el cálculo: corrige las observaciones del Resumen para generar el PDF.'
        : null;

    // Opciones del modelo (Mock)
    const [opcionesEquipos, setOpcionesEquipos] = useState<IOpcionesEquiposNBS | null>(null);
    // En pantallas pequeñas el modal de detalle se abre a pantalla completa.
    const esPantallaPequena = useMediaQuery('(max-width: 48em)') ?? false;
    React.useEffect(() => {
        const fetchOpciones = async () => {
            const modelo = new CotizadorModelo();
            const opciones = await modelo.obtenerOpcionesEquipos();
            setOpcionesEquipos(opciones);
        };
        fetchOpciones();
    }, []);

    // Moneda del componente local, márgenes y comisión: COP en Colombia, USD en países en dólares.
    const monedaLocal = cotizacion.MonedaLocal ?? 'COP';


    const handleAutosave = async () => {
        if (alAutoguardar) {
            setEstaAutoguardando(true);
            setEstaGuardado(false);
            setErrorGuardado(false);
            try {
                const result = alAutoguardar?.();
                if (result !== undefined && result !== null && typeof (result as Promise<void>).then === 'function') {
                    await result;
                }
                setEstaAutoguardando(false);
                setEstaGuardado(true);
            } catch (error) {
                setEstaAutoguardando(false);
                setErrorGuardado(true);
            }
        }
    };
    const { mostrarNotificacion } = useNotificaciones();
    const { tienePermiso } = usePermisos();
    const puedeExtenderMantenimiento = tienePermiso(EnumPermisos.PERMISO_COTIZADOR_NBS_MANTENIMIENTO_EXTENDIDO);

    const siguientePaso = async () => {
        let hasErrors = false;
        const nuevosErrores: Record<string, string> = {};

        if (active === 0) {
            if (!cotizacion.Proyecto.NombreCliente?.trim()) {
                nuevosErrores.NombreCliente = "Campo obligatorio";
                hasErrors = true;
            }
            if (!cotizacion.Proyecto.Nombre) {
                nuevosErrores.Nombre = "Campo obligatorio";
                hasErrors = true;
            }
            if (!cotizacion.Proyecto.Ciudad) {
                nuevosErrores.Ciudad = "Campo obligatorio";
                hasErrors = true;
            }
            if (!cotizacion.Proyecto.Anio) {
                nuevosErrores.Anio = "Campo obligatorio";
                hasErrors = true;
            }
            if (!cotizacion.Proyecto.MesesContrato || cotizacion.Proyecto.MesesContrato === 0) {
                nuevosErrores.MesesContrato = "Campo obligatorio";
                hasErrors = true;
            }
            if (!cotizacion.Proyecto.MesesMantenimiento) {
                nuevosErrores.MesesMantenimiento = "Campo obligatorio";
                hasErrors = true;
            } else if (!mesesMantenimientoValidos(cotizacion.Proyecto.MesesMantenimiento, puedeExtenderMantenimiento)) {
                nuevosErrores.MesesMantenimiento = puedeExtenderMantenimiento ? "Elige 3, 6, 9, 12 o más meses" : "Elige 3, 6, 9 o 12 meses";
                hasErrors = true;
            }
            // Plazos y garantías de la oferta: obligatorios (las opciones de plazo ya las limita la lista).
            for (const campo of ['PlazoEntregaMeses', 'MesesInstalacion', 'GarantiaFabricacionMeses', 'GarantiaInstalacionMeses'] as const) {
                const meses = cotizacion.Proyecto[campo];
                if (meses === null || meses === undefined || meses <= 0) {
                    nuevosErrores[campo] = "Campo obligatorio";
                    hasErrors = true;
                }
            }

            if (hasErrors) {
                setErrores(nuevosErrores);
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    "Campos requeridos (*)",
                    "Por favor completa todos los campos antes de continuar."
                );
                return;
            }

            // Crea la cotización o guarda los cambios del proyecto (en solo lectura el controlador no hace nada).
            if (alAvanzarPaso1) {
                setEstaAutoguardando(true);
                setErrorGuardado(false);
                try {
                    await alAvanzarPaso1();
                    setEstaAutoguardando(false);
                    setEstaGuardado(true);
                } catch (e) {
                    setEstaAutoguardando(false);
                    setErrorGuardado(true);
                    return;
                }
            }
        }
        if (active === 1) {
            if (cotizacion.Ascensores.length === 0 && cotizacion.Escaleras.length === 0) {
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    "Equipos requeridos",
                    "Debes agregar al menos un equipo (Ascensor o Escalera) para continuar."
                );
                return; // Bloquea si no hay equipos
            }
            if (alAvanzarPaso2) {
                setCalculandoTotales(true);
                try {
                    await alAvanzarPaso2();
                    setCalculandoTotales(false);
                } catch (e) {
                    setCalculandoTotales(false);
                    return;
                }
            }
        }

        setErrores({});
        setActive((current) => (current < 2 ? current + 1 : current));
        handleAutosave();
    };
    const pasoAnterior = () => {
        setErrores({});
        setActive((current) => (current > 0 ? current - 1 : current));
        handleAutosave();
    };

    // Estado para modal de detalles (Resultados)
    const [modalDetalleAbierto, setModalDetalleAbierto] = useState(false);
    const [equipoSeleccionado, setEquipoSeleccionado] = useState<IEquipoAscensor | IEquipoEscalera | null>(null);
    const [cantidadContenedoresModal, setCantidadContenedoresModal] = useState(0);
    const [editandoContenedores, setEditandoContenedores] = useState(false);
    const [guardandoContenedores, setGuardandoContenedores] = useState(false);
    const [errores, setErrores] = useState<Record<string, string>>({});

    /** Contenedores con los que se calculó el ascensor: el manual si existe, si no el calculado. */
    const contenedoresEfectivos = (equipo: IEquipoAscensor | IEquipoEscalera) =>
        (equipo.resultados?.CantidadContenedoresManual || 0) > 0
            ? equipo.resultados!.CantidadContenedoresManual!
            : equipo.resultados?.CantidadContenedoresCalculada ?? 0;

    // Tras recalcular, el detalle abierto debe mostrar los resultados nuevos del mismo equipo.
    React.useEffect(() => {
        if (!equipoSeleccionado) return;
        const actualizado = [...cotizacion.Ascensores, ...cotizacion.Escaleras].find(e => e.Id === equipoSeleccionado.Id);
        if (actualizado && actualizado !== equipoSeleccionado) {
            setEquipoSeleccionado(actualizado);
            if (cotizacion.Ascensores.some(a => a.Id === actualizado.Id)) setCantidadContenedoresModal(contenedoresEfectivos(actualizado));
        }
    }, [cotizacion]);

    const cancelarEdicionContenedores = () => {
        if (equipoSeleccionado) setCantidadContenedoresModal(contenedoresEfectivos(equipoSeleccionado));
        setEditandoContenedores(false);
    };

    const guardarContenedores = async (cantidad: number | null) => {
        if (!equipoSeleccionado || !alActualizarContenedores) return;
        const actual = contenedoresEfectivos(equipoSeleccionado);
        if (cantidad !== null && (!(cantidad > 0) || cantidad === actual)) {
            // Sin cambios o valor inválido: no se recalcula.
            cancelarEdicionContenedores();
            return;
        }
        setGuardandoContenedores(true);
        const guardado = await alActualizarContenedores(equipoSeleccionado.Id, cantidad);
        setGuardandoContenedores(false);
        if (guardado) setEditandoContenedores(false);
    };

    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100vh', overflow: 'hidden' }}>
            <LoadingOverlay visible={calculandoTotales} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} loaderProps={{ color: 'primary' }} />
            {/* Cabecera compacta: título y estado en una línea, para dejar más alto al contenido. */}
            <Box
                pb="xs"
                pt="sm"
                style={{
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'var(--mantine-color-body)',
                    paddingLeft: '20px',
                    paddingRight: '20px'
                }}
            >
                <Group justify="space-between">
                    <div>
                        <Group gap="sm">
                            <Title order={4}>{cotizacion.Id ? (esSoloLectura ? 'Consultar Cotización NBS' : 'Editar Cotización NBS') : 'Nueva Cotización NBS'}{cotizacion.Codigo ? ` ${cotizacion.Codigo}` : ''}</Title>
                            {cotizacion.Id && cotizacion.Estado?.Id != null && (
                                // Clic en la etiqueta: cambiar estado (mismo componente que la tabla).
                                <BadgeEstadoCotizacionVista
                                    size="sm"
                                    estadoId={cotizacion.Estado.Id as number}
                                    transiciones={cotizacion.TransicionesPermitidas}
                                    alCambiarEstado={alCambiarEstado}
                                />
                            )}
                            {cotizacion.Id && (
                                <Tooltip label="Historial de estados" withArrow>
                                    <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => setHistorialAbierto(true)}>
                                        <IconHistory size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            {estaAutoguardando && (
                                <Tooltip label="Guardando..." position="right" withArrow>
                                    <Loader size={18} color="dimmed" />
                                </Tooltip>
                            )}
                            {active !== 2 && estaGuardado && !estaAutoguardando && !errorGuardado && (
                                <Tooltip label="Guardado" position="right" withArrow>
                                    <IconCloudCheck size={20} color="gray" style={{ marginTop: '2px' }} />
                                </Tooltip>
                            )}
                            {errorGuardado && !estaAutoguardando && (
                                <Tooltip label="Error al guardar" position="right" withArrow>
                                    <IconCloudX size={20} color="red" style={{ marginTop: '2px' }} />
                                </Tooltip>
                            )}
                        </Group>
                    </div>
                    <Group>
                        <Button variant="light" color="primary" size="xs" radius="lg" leftSection={<IconX size={16} />} onClick={alCancelar}>
                            Cancelar
                        </Button>
                    </Group>
                </Group>
            </Box>

            {/* Stepper y Contenido */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 20px 20px 20px', minHeight: 0 }}>
                {/* flex 1 1 auto: el alto se ajusta al contenido y solo hay scroll si no cabe en la pantalla. */}
                <Card shadow="none" withBorder radius="md" pb="xs" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Stepper
                        active={active}
                        onStepClick={setActive}
                        allowNextStepsSelect={false}
                        size="sm"
                        mb="md"
                        styles={{
                            stepIcon: {
                                fontWeight: 'bold',
                                '&[data-progress]': {
                                    color: 'var(--mantine-color-primary-filled) !important'
                                }
                            }
                        }}
                    >
                        <Stepper.Step label="Proyecto" description="Datos generales" />
                        <Stepper.Step label="Equipos" description="Ascensores y Escaleras" />
                        <Stepper.Step label="Resumen" description="Verificación" />
                    </Stepper>

                    <Box style={{ flex: '1 1 auto', overflowY: 'auto', paddingRight: '10px', marginBottom: '8px', minHeight: 0 }}>
                        {/* Paso 1: Datos del Proyecto */}
                        {active === 0 && (
                            <PasoProyectoVista
                                cotizacion={cotizacion}
                                alCambiarProyecto={alCambiarProyecto}
                                errores={errores}
                                setErrores={setErrores}
                                ciudades={ciudades || []}
                                estaAutoguardando={estaAutoguardando}
                                esAceptada={esSoloLectura}
                                puedeExtenderMantenimiento={puedeExtenderMantenimiento}
                                esPanama={esPanama}
                            />
                        )}

                        {/* Paso 2: Equipos */}
                        {active === 1 && (
                            <PasoEquiposVista
                                cotizacion={cotizacion}
                                opcionesEquipos={opcionesEquipos}
                                esAceptada={esSoloLectura}
                                eliminarEquipo={eliminarEquipo}
                                editarAscensor={editarAscensor}
                                agregarAscensor={agregarAscensor}
                                editarEscalera={editarEscalera}
                                agregarEscalera={agregarEscalera}
                                setEstaGuardado={setEstaGuardado}
                                setErrorGuardado={setErrorGuardado}
                                setEstaAutoguardando={setEstaAutoguardando}
                            />
                        )}

                        {active === 2 && (
                        <PasoResumenVista
                            cotizacion={cotizacion}
                            esSoloLectura={esSoloLectura}
                            alGuardarConfiguracion={alGuardarConfiguracion}
                            alUsarConfiguracionGlobal={alUsarConfiguracionGlobal}
                            alRecalcularConMatricesVigentes={alRecalcularConMatricesVigentes}
                            setEquipoSeleccionado={setEquipoSeleccionado}
                            setCantidadContenedoresModal={setCantidadContenedoresModal}
                            setModalDetalleAbierto={setModalDetalleAbierto}
                        />
                    )}
                    </Box>
                    {/* Controles de Navegación del Stepper (compactos: más alto para el contenido). */}
                    <Box style={{ paddingTop: '8px', marginTop: 'auto', borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                        <Group justify="space-between">
                            <Button variant="light" color="primary" size="xs" radius="lg" onClick={pasoAnterior} disabled={active === 0 || estaAutoguardando} leftSection={<IconArrowLeft size={16} />}>
                                Anterior
                            </Button>

                            <Group gap="xs" justify="flex-end">
                                {active < 2 ? (
                                    <Button size="xs" radius="xl" onClick={siguientePaso} loading={estaAutoguardando} rightSection={<IconArrowRight size={16} />}>
                                        Siguiente
                                    </Button>
                                ) : (
                                    // PDF: cotización ya generada (no Borrador) y con el último cálculo completo. Abre la
                                    // previsualización, desde donde se descarga. En Aceptada sale con los valores congelados.
                                    <Tooltip label={motivoSinPdf ?? ''} disabled={!motivoSinPdf} withArrow multiline w={260}>
                                        <Button
                                            color="primary"
                                            size="xs"
                                            radius="xl"
                                            onClick={alGuardar}
                                            disabled={!!motivoSinPdf}
                                            leftSection={<IconFileTypePdf size={16} />}
                                        >
                                            {esSoloLectura ? 'Ver PDF' : 'Generar PDF'}
                                        </Button>
                                    </Tooltip>
                                )}
                            </Group>
                        </Group>
                    </Box>
                </Card>
            </div>

            {cotizacion.Id && (
                <HistorialEstadosCV cotizacionId={cotizacion.Id} abierto={historialAbierto} alCerrar={() => setHistorialAbierto(false)} />
            )}

            {/* Modal de Desglose de Resultados */}
            <Modal
                opened={modalDetalleAbierto}
                onClose={() => { if (guardandoContenedores) return; setEditandoContenedores(false); setModalDetalleAbierto(false); }}
                fullScreen={esPantallaPequena}
                styles={{
                    title: { width: '100%' },
                    inner: { paddingBottom: esPantallaPequena ? 0 : '3rem' },
                    // El contenido no hace scroll completo: título y pestañas quedan fijos
                    // y solo los paneles (dentro del Tabs) hacen scroll.
                    content: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
                    header: { flexShrink: 0 },
                    body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: 0 }
                }}
                title={
                    <Group justify="space-between" align="center" gap="xs" wrap="wrap" style={{ width: '100%', paddingRight: '20px' }}>
                        <Title order={5} c="primary">Detalle Costos Equipo</Title>
                        {equipoSeleccionado && cotizacion.UsaTrm !== false && (
                            <Badge color="primary" variant="light" radius="xl" size="lg" style={{ textTransform: "none" }}>TRM: $ {(cotizacion.Proyecto.Trm ?? 0).toLocaleString('es-CO')} COP</Badge>
                        )}
                    </Group>
                }
                size="xl"
            >
                {equipoSeleccionado ? (() => {
                    const esAscensor = cotizacion.Ascensores.some(a => a.Id === equipoSeleccionado.Id);
                    const fcaUSD = equipoSeleccionado.CostoFCA || 0;

                    const resultados = equipoSeleccionado.resultados ?? RESULTADOS_VACIOS;
                    const fleteUnitario = resultados.FleteUnitarioXContainer ?? 0;
                    const fobUnitario = resultados.FobUnitario ?? 0;
                    const valorFleteUnitario = resultados.ValorFleteUnitario ?? 0;
                    const sia = resultados.Sia ?? 0;
                    const seguroFCA = resultados.Seguro ?? 0;
                    const bodegajes = resultados.Montacargas ?? 0;
                    const fleteNacional = resultados.FleteNacional ?? 0;
                    const subTotalOtros = resultados.SubTotalOtrosImportados ?? 0;
                    const costoFCAConDescuentoKone = resultados.CostoFCAConDescuentoKone ?? 0;
                    const subTotalImportados = resultados.SubTotalImportados ?? 0;
                    const totalImportadoSinDescuento = resultados.TotalImportadoSinDescuento ?? 0;
                    const comisionCompras = resultados.Comision ?? 0;
                    const upme = resultados.Upme ?? 0;
                    const polizas = resultados.Polizas ?? 0;
                    const totalImportado = resultados.TotalImportado ?? 0;
                    const diasInstalacion = resultados.DiasInstalacion ?? 0;
                    const moi = resultados.Moi ?? 0;
                    const fic = resultados.Fic ?? 0;
                    const cif = resultados.Cif ?? 0;
                    const subTotalManoObra = resultados.SubTotalManoObra ?? 0;
                    const bateriasOtrosLocales = esAscensor ? resultados.BateriasEbd ?? 0 : 0;
                    const transformador = resultados.Transformador ?? 0;
                    const desembalaje = resultados.Desembalaje ?? 0;
                    const montacargasTrasiegos = resultados.MontacargasTrasiegos ?? 0;
                    const insumosInstalacion = resultados.InsumosInstalacion ?? 0;
                    const herramientas = esAscensor ? 0 : resultados.BateriasEbd ?? 0;
                    const certificacionNTC = resultados.CertificacionNTC ?? 0;
                    const imprevistos = esAscensor ? resultados.Imprevistos ?? 0 : 0;
                    // El back ya lo calcula (incluye Imprevistos solo en ascensores); no se recalcula aquí.
                    const subTotalInstalacion = resultados.ComponenteSinInstalacion ?? 0;
                    const viaticos = resultados.Viaticos ?? 0;
                    const desplazamientos = resultados.Desplazamientos ?? 0;
                    const mttoFreeOfrecido = resultados.MttoFreeOfrecido ?? 0;
                    const totalLocal = resultados.TotalLocal ?? 0;
                    const totalLocalSinDescuento = resultados.TotalLocalSinDescuento ?? 0;
                    const comisionComercial = resultados.ComisionComercial ?? 0;
                    const margenImportadoPorcentaje = resultados.MargenImportadoPorcentaje ?? 0;
                    const margenImportadoPesos = resultados.MargenImportadoPesos ?? 0;
                    const margenLocalPorcentaje = resultados.MargenLocalPorcentaje ?? 0;
                    const margenLocalPesos = resultados.MargenLocalPesos ?? 0;
                    const precioVentaSinImpuestos = resultados.PrecioVentaSinImpuestos ?? 0;
                    // Una sola TRM (solo países con moneda local distinta del dólar).
                    const usaTrm = cotizacion.UsaTrm !== false;
                    const trmEquipo = cotizacion.Proyecto.Trm || 0;
                    // Informativo: el precio de venta del equipo también en USD (no cambia la cotización).
                    const precioVentaUsd = usaTrm && trmEquipo > 0 ? precioVentaSinImpuestos / trmEquipo : null;
                    const abrirVariacionMensual = () => setEquipoVariacion({
                        Nombre: equipoSeleccionado.Nombre || '-',
                        FleteUnitario: valorFleteUnitario,
                        // En USD, como el flete: en Colombia el precio en COP / TRM; en países en dólares ya es USD.
                        PrecioVentaUsd: usaTrm ? (precioVentaUsd ?? 0) : precioVentaSinImpuestos
                    });

                    return (
                        // Pestañas fijas: el Tabs es una columna flex; la lista no se encoge y solo
                        // el contenedor de los paneles hace scroll.
                        <Tabs defaultValue="general" variant="default" radius="md" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                            <Tabs.List grow={!esPantallaPequena} justify="center" mb="md" style={{ flexWrap: 'wrap', flexShrink: 0 }}>
                                <Tabs.Tab value="general" leftSection={<IconBriefcase size={16} />}>
                                    Datos Generales
                                </Tabs.Tab>
                                <Tabs.Tab value="importado" leftSection={<IconTruck size={16} />}>
                                    Importación y Fletes
                                </Tabs.Tab>
                                <Tabs.Tab value="local" leftSection={<IconBuildingSkyscraper size={16} />}>
                                    Componente Local
                                </Tabs.Tab>
                                <Tabs.Tab value="rentabilidad" leftSection={<IconStairs size={16} />}>
                                    Rentabilidad
                                </Tabs.Tab>
                            </Tabs.List>

                            {/* div (no Box): el CSS global en móvil fuerza overflow: visible en .mantine-Box-root.
                                overflowX hidden: el Grid de Mantine sobresale medio gutter por lado (margen negativo)
                                y generaba scroll horizontal; lo que se recorta es solo el padding de las columnas. */}
                            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingBottom: 'var(--mantine-spacing-md)' }}>

                            <Tabs.Panel value="general" pt="xs">
                                <Grid>
                                    <Grid.Col span={12}>
                                        <Card withBorder shadow="sm" radius="md" padding="lg" bg="white">
                                            <Group mb="md">
                                                <ThemeIcon variant="light" size="lg" radius="xl" color="primary">
                                                    <IconEye size={20} />
                                                </ThemeIcon>
                                                <Title order={5} c="dark.7" tt="uppercase">Especificaciones del Equipo</Title>
                                            </Group>

                                            <Stack gap="xs">
                                                {/* Etiquetas iguales a las del formulario de equipos (PasoEquiposVista) */}
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Nombre:</Text><Text size="sm" fw={600}>{equipoSeleccionado.Nombre}</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Tipo:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as any).Tipo || 'N/A'}</Text></Group>
                                                {esAscensor && <Group justify="space-between"><Text size="sm" c="dimmed">Uso:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoAscensor).Uso || 'N/A'}</Text></Group>}
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Modelo:</Text><Text size="sm" fw={600}>{equipoSeleccionado.ProductoNombre || 'N/A'}</Text></Group>
                                                {esAscensor ? (
                                                    <>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Capacidad:</Text><Text size="sm" fw={600}>{textoCapacidadAscensor((equipoSeleccionado as IEquipoAscensor).Capacidad, (equipoSeleccionado as IEquipoAscensor).Pasajeros)}</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Velocidad:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoAscensor).VelocidadIngreso || 0} m/s</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Operador de puerta:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoAscensor).OperadorPuertaNombre || 'N/A'}</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Paradas:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoAscensor).Paradas || 0}</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Máquina:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoAscensor).MaquinaNombre || 'N/A'}</Text></Group>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Contenedores:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoEscalera).Contenedores || 0}</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Velocidad:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoEscalera).VelocidadIngreso || 0} m/s</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Altura:</Text><Text size="sm" fw={600}>{((equipoSeleccionado as IEquipoEscalera).Altura || 0).toLocaleString('es-CO')} mm</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Ancho de pasos:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoEscalera).AnchoPasos || 0} mm</Text></Group>
                                                        <Group justify="space-between"><Text size="sm" c="dimmed">Inclinación:</Text><Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoEscalera).Inclinacion || 0}°</Text></Group>
                                                    </>
                                                )}
                                                <Group justify="space-between"><Text size="sm" c="dimmed">P. Motor:</Text><Text size="sm" fw={600}>{equipoSeleccionado.PotenciaMotor || 0} kW</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Cantidad:</Text><Text size="sm" fw={600}>{equipoSeleccionado.Cantidad || 1}</Text></Group>

                                                <Divider my="sm" variant="dashed" />

                                                <Box p="sm">
                                                    <Stack gap="xs">
                                                        <Group justify="space-between">
                                                            <Text size="md" fw={700} c="dark.7" tt="uppercase">Costo FCA:</Text>
                                                            <Badge size="lg" variant="dot" color="primary">$ {fcaUSD.toLocaleString('es-CO')} USD</Badge>
                                                        </Group>
                                                        <Group justify="space-between">
                                                            <Text size="md" fw={700} c="dark.7" tt="uppercase">Costo con Dto. KONE:</Text>
                                                            <Badge size="lg" variant="filled" color="#40c057">$ {costoFCAConDescuentoKone.toLocaleString('es-CO')} USD</Badge>
                                                        </Group>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>
                                </Grid>
                            </Tabs.Panel>

                            <Tabs.Panel value="importado" pt="xs">
                                <Grid align="stretch">
                                    {/* Fila 1 */}
                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card withBorder shadow="sm" radius="md" padding="md" bg="white" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">Detalles de Costos Flete</Title>
                                            <Stack gap={6} style={{ flex: 1 }}>
                                                <Group justify="space-between" align="center">
                                                    <Text size="sm" c="dimmed">Cantidad Contenedores:</Text>
                                                    {esAscensor ? (() => {
                                                        const contenedoresManual = (resultados.CantidadContenedoresManual || 0) > 0;
                                                        const contenedoresCalculados = resultados.CantidadContenedoresCalculada ?? 0;
                                                        return (
                                                            <Group gap={4}>
                                                                {!editandoContenedores ? (
                                                                    <>
                                                                        {contenedoresManual && (
                                                                            <Tooltip label={`Editado manualmente. Calculado: ${contenedoresCalculados.toLocaleString('es-CO')}`} withArrow>
                                                                                <Badge color="primary" variant="light" size="sm" radius="xl" style={{ textTransform: "none" }}>Manual</Badge>
                                                                            </Tooltip>
                                                                        )}
                                                                        <Text size="sm" fw={600}>{cantidadContenedoresModal.toLocaleString('es-CO')}</Text>
                                                                        {!esSoloLectura && alActualizarContenedores && (
                                                                            <>
                                                                                <Tooltip label="Editar contenedores" withArrow>
                                                                                    <ActionIcon variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => setEditandoContenedores(true)}>
                                                                                        <IconEdit size={18} />
                                                                                    </ActionIcon>
                                                                                </Tooltip>
                                                                                {contenedoresManual && (
                                                                                    <Tooltip label={`Volver al calculado (${contenedoresCalculados.toLocaleString('es-CO')})`} withArrow>
                                                                                        <ActionIcon variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" loading={guardandoContenedores} onClick={() => guardarContenedores(null)}>
                                                                                            <IconRestore size={18} />
                                                                                        </ActionIcon>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <Group gap={4}>
                                                                        <NumberInput
                                                                            min={0.01}
                                                                            max={999}
                                                                            decimalScale={2}
                                                                            decimalSeparator=","
                                                                            allowNegative={false}
                                                                            size="xs"
                                                                            w={80}
                                                                            autoFocus
                                                                            disabled={guardandoContenedores}
                                                                            value={cantidadContenedoresModal}
                                                                            onChange={(val) => setCantidadContenedoresModal(Number(val) || 0)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') guardarContenedores(cantidadContenedoresModal);
                                                                                if (e.key === 'Escape') { e.stopPropagation(); cancelarEdicionContenedores(); }
                                                                            }}
                                                                        />
                                                                        <Tooltip label="Guardar y recalcular" withArrow>
                                                                            <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" loading={guardandoContenedores} disabled={!(cantidadContenedoresModal > 0)} onClick={() => guardarContenedores(cantidadContenedoresModal)}>
                                                                                <IconCheck size={18} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                        <Tooltip label="Cancelar" withArrow>
                                                                            <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" disabled={guardandoContenedores} onClick={cancelarEdicionContenedores}>
                                                                                <IconX size={18} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    </Group>
                                                                )}
                                                            </Group>
                                                        );
                                                    })() : (
                                                        <Text size="sm" fw={600}>{(equipoSeleccionado as IEquipoEscalera).Contenedores || 0}</Text>
                                                    )}
                                                </Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Flete Unitario (x Container):</Text><Text size="sm" fw={600}>$ {fleteUnitario.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">FOB Unitario:</Text><Text size="sm" fw={600}>$ {fobUnitario.toLocaleString('es-CO')} USD</Text></Group>
                                                <Divider my={4} variant="dashed"/>

                                                <Box mt="auto" p="sm">
                                                    <Group justify="space-between"><Text size="sm" fw={700} c="dark.7">VALOR FLETE UNITARIO:</Text><Text size="sm" fw={700} c="primary">$ {valorFleteUnitario.toLocaleString('es-CO')} USD</Text></Group>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>

                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card withBorder shadow="sm" radius="md" padding="md" bg="white" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">{esAscensor ? 'Sub-Total Componente Importado' : 'Componente Importado'}</Title>
                                            <Stack gap={6} style={{ flex: 1 }}>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Costo Equipo (Vr. Unitario):</Text><Text size="sm" fw={600}>$ {costoFCAConDescuentoKone.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Sub-Total Otros Importados:</Text><Text size="sm" fw={600}>$ {subTotalOtros.toLocaleString('es-CO')} USD</Text></Group>
                                                <Divider my={4} variant="dashed"/>

                                                <Box mt="auto" p="sm">
                                                    <Group justify="space-between"><Text size="sm" fw={700} c="dark.7">SUB-TOTAL IMPORTADOS:</Text><Text size="sm" fw={700} c="primary">$ {subTotalImportados.toLocaleString('es-CO')} USD</Text></Group>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>

                                    {/* Fila 2 */}
                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card withBorder shadow="sm" radius="md" padding="md" bg="white" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">Componente Importado Del Flete</Title>
                                            <Stack gap={6} style={{ flex: 1 }}>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">SIA:</Text><Text size="sm" fw={600}>$ {sia.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Seguro:</Text><Text size="sm" fw={600}>$ {seguroFCA.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Montacargas:</Text><Text size="sm" fw={600}>$ {bodegajes.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Flete Nacional:</Text><Text size="sm" fw={600}>$ {fleteNacional.toLocaleString('es-CO')} USD</Text></Group>
                                                <Divider my={4} variant="dashed"/>

                                                <Box mt="auto" p="sm">
                                                    <Group justify="space-between"><Text size="sm" fw={700} c="dark.7">TOTAL:</Text><Text size="sm" fw={700} c="primary">$ {subTotalOtros.toLocaleString('es-CO')} USD</Text></Group>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>

                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card withBorder shadow="sm" radius="md" padding="md" bg="white" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">Componente Importado - Con Utilidad</Title>
                                            <Stack gap={6} style={{ flex: 1 }}>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Total Importado (Sin descuento):</Text><Text size="sm" fw={600}>$ {totalImportadoSinDescuento.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Comisión:</Text><Text size="sm" fw={600}>$ {comisionCompras.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">UPME:</Text><Text size="sm" fw={600}>$ {upme.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Pólizas:</Text><Text size="sm" fw={600}>$ {polizas.toLocaleString('es-CO')} USD</Text></Group>
                                                <Divider my={4} variant="dashed"/>

                                                <Box mt="auto" p="sm">
                                                    <Group justify="space-between">
                                                        <Text size="md" fw={700} c="dark.7">TOTAL IMPORTADO:</Text>
                                                        <Badge size="lg" variant="filled" color="primary">$ {totalImportado.toLocaleString('es-CO')} USD</Badge>
                                                    </Group>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>
                                </Grid>
                            </Tabs.Panel>

                            <Tabs.Panel value="local" pt="xs">
                                <Grid align="stretch">
                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Stack h="100%" justify="stretch">
                                            <Card withBorder shadow="sm" radius="md" padding="md" bg="white" style={{ flex: 1, minHeight: 'min-content' }}>
                                                <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">Componente Local - Mano De Obra</Title>
                                                <Stack gap={6} style={{ flex: 1 }}>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Días de Instalación:</Text><Text size="sm" fw={600}>{diasInstalacion}</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">M.O.D.:</Text><Text size="sm" fw={600}>$ {moi.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">FIC / SENA:</Text><Text size="sm" fw={600}>$ {fic.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Impuestos CIF / ICA:</Text><Text size="sm" fw={600}>$ {cif.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Divider my={4} variant="dashed"/>

                                                    <Box mt="auto" p="sm">
                                                        <Group justify="space-between"><Text size="sm" fw={700} c="dark.7">SUB-TOTAL MANO DE OBRA:</Text><Text size="sm" fw={700} c="primary">$ {subTotalManoObra.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    </Box>
                                                </Stack>
                                            </Card>

                                            <Card withBorder shadow="sm" radius="md" padding="md" bg="white" style={{ flex: 1, minHeight: 'min-content' }}>
                                                <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">Componente Local</Title>
                                                <Stack gap={6} style={{ flex: 1 }}>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Total Local (Sin descuento):</Text><Text size="sm" fw={600}>$ {totalLocalSinDescuento.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Viáticos:</Text><Text size="sm" fw={600}>$ {viaticos.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Desplazamientos:</Text><Text size="sm" fw={600}>$ {desplazamientos.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">MTTO. Free Ofrecido:</Text><Text size="sm" fw={600}>$ {mttoFreeOfrecido.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    <Divider my={4} variant="dashed"/>

                                                    <Box mt="auto" p="sm">
                                                        <Group justify="space-between"><Text size="sm" fw={700} c="dark.7" tt="uppercase">TOTAL COMPONENTE LOCAL:</Text><Text size="sm" fw={700} c="primary">$ {totalLocal.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                    </Box>
                                                </Stack>
                                            </Card>
                                        </Stack>
                                    </Grid.Col>

                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card h="100%" withBorder shadow="sm" radius="md" padding="md" bg="white" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.5" fw={700} mb="sm" tt="uppercase">Componentes Instalación</Title>
                                            <Stack gap={6} style={{ flex: 1 }}>
                                                {esAscensor && <Group justify="space-between"><Text size="sm" c="dimmed">Baterías EBD:</Text><Text size="sm" fw={600}>$ {bateriasOtrosLocales.toLocaleString('es-CO')} {monedaLocal}</Text></Group>}
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Transformador:</Text><Text size="sm" fw={600}>$ {transformador.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Desembalaje:</Text><Text size="sm" fw={600}>$ {desembalaje.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Montacargas para Trasiegos:</Text><Text size="sm" fw={600}>$ {montacargasTrasiegos.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Insumos de Instalación:</Text><Text size="sm" fw={600}>$ {insumosInstalacion.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                {!esAscensor && <Group justify="space-between"><Text size="sm" c="dimmed">Herramientas:</Text><Text size="sm" fw={600}>$ {herramientas.toLocaleString('es-CO')} {monedaLocal}</Text></Group>}
                                                <Group justify="space-between"><Text size="sm" c="dimmed">Certificación:</Text><Text size="sm" fw={600}>$ {certificacionNTC.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                                {esAscensor && <Group justify="space-between"><Text size="sm" c="dimmed">Imprevistos:</Text><Text size="sm" fw={600}>$ {imprevistos.toLocaleString('es-CO')} {monedaLocal}</Text></Group>}
                                                <Divider my={4} variant="dashed"/>

                                                <Box mt="auto" p="sm">
                                                    <Group justify="space-between">
                                                        <Text size="md" fw={700} c="dark.7">TOTAL EN {monedaLocal}:</Text>
                                                        <Badge size="lg" variant="filled" color="primary">$ {subTotalInstalacion.toLocaleString('es-CO')} {monedaLocal}</Badge>
                                                    </Group>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>
                                </Grid>
                            </Tabs.Panel>

                            <Tabs.Panel value="rentabilidad" pt="xs">
                                {/* La variación mensual afecta al flete y al precio de venta: una acción para toda la pestaña. */}
                                <Group justify="flex-end" mb="sm">
                                    <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconCalendarStats size={14} />} onClick={abrirVariacionMensual}>
                                        <Text fw={500} size="xs">Variación Mensual Flete</Text>
                                    </Button>
                                </Group>
                                <Grid align="stretch">
                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card h="100%" withBorder shadow="sm" radius="md" padding="xl" bg="white" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.7" mb="xl" tt="uppercase" fz="sm">Valores Cotizador</Title>
                                            <Stack gap="lg" style={{ flex: 1 }}>
                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">TOTAL COMPONENTE IMPORTADO:</Text><Text size="sm" fw={700} c="dark.9">$ {totalImportado.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">VALOR FLETE UNITARIO:</Text><Text size="sm" fw={700} c="dark.9">$ {valorFleteUnitario.toLocaleString('es-CO')} USD</Text></Group>
                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">TOTAL COMPONENTE LOCAL:</Text><Text size="sm" fw={700} c="dark.9">$ {totalLocal.toLocaleString('es-CO')} {monedaLocal}</Text></Group>

                                                <Box mt="auto">
                                                    <Card withBorder radius="md" padding="md" style={{ backgroundColor: 'var(--mantine-color-primary-light)', borderColor: 'var(--mantine-color-primary-outline)', borderStyle: 'dashed' }}>
                                                        <Stack gap={4}>
                                                            <Text size="xs" fw={700} c="primary" tt="uppercase">COMISIÓN COMERCIAL:</Text>
                                                            <Text size="lg" fw={800} c="primary">$ {comisionComercial.toLocaleString('es-CO')} {monedaLocal}</Text>
                                                        </Stack>
                                                    </Card>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>

                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card h="100%" withBorder shadow="sm" radius="md" padding="xl" bg="white" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Title order={6} c="dark.7" mb="xl" tt="uppercase" fz="sm">Valores Cotizador (Márgenes)</Title>
                                            <Stack gap="lg" style={{ flex: 1 }}>
                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">MARGEN IMPORTADO (EN PORCENTAJE):</Text><Text size="sm" fw={700} c="dark.9">{(margenImportadoPorcentaje * 100).toFixed(2)} %</Text></Group>
                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">MARGEN IMPORTADO (EN {monedaLocal === 'COP' ? 'PESOS' : monedaLocal}):</Text><Text size="sm" fw={700} c="dark.9">$ {margenImportadoPesos.toLocaleString('es-CO')} {monedaLocal}</Text></Group>

                                                <Divider my={0} variant="dashed" color="gray.4"/>

                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">MARGEN LOCAL (EN PORCENTAJE):</Text><Text size="sm" fw={700} c="dark.9">{(margenLocalPorcentaje * 100).toFixed(2)} %</Text></Group>
                                                <Group justify="space-between"><Text size="xs" c="dark.7" fw={600} tt="uppercase">MARGEN LOCAL (EN {monedaLocal === 'COP' ? 'PESOS' : monedaLocal}):</Text><Text size="sm" fw={700} c="dark.9">$ {margenLocalPesos.toLocaleString('es-CO')} {monedaLocal}</Text></Group>

                                                <Stack gap="sm" mt="auto">
                                                    <Card radius="md" padding="md" bg="primary" style={{ color: 'white' }}>
                                                        <Stack gap={4} align="center">
                                                            <Text size="xs" fw={700} opacity={0.9} tt="uppercase">Precio de venta sin impuestos</Text>
                                                            <Text size="xl" fw={800}>$ {precioVentaSinImpuestos.toLocaleString('es-CO')} {monedaLocal}</Text>
                                                        </Stack>
                                                    </Card>
                                                    {precioVentaUsd !== null && (
                                                        <Card withBorder radius="md" padding="md" style={{ backgroundColor: 'var(--mantine-color-primary-light)', borderColor: 'var(--mantine-color-primary-outline)', borderStyle: 'dashed' }}>
                                                            <Stack gap={2} align="center">
                                                                <Text size="xs" fw={700} c="primary" tt="uppercase">Precio de venta sin impuestos (USD)</Text>
                                                                <Text size="lg" fw={800} c="primary">$ {precioVentaUsd.toLocaleString('es-CO', { maximumFractionDigits: 2 })} USD</Text>
                                                            </Stack>
                                                        </Card>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>
                                </Grid>
                            </Tabs.Panel>
                            </div>
                        </Tabs>
                    );
                })() : null}
            </Modal>

            {/* Por encima del detalle del equipo, desde el que se abre. */}
            <ModalVariacionMensualVista
                equipo={equipoVariacion}
                alCerrar={() => setEquipoVariacion(null)}
                variacion={variacionMensual}
                zIndex={1000}
            />
        </div>
    );
};
